import React, { useEffect, useState } from 'react';
import { IonButton, IonIcon, IonSpinner, IonText } from '@ionic/react';
import { checkmarkCircle } from 'ionicons/icons';
import HkModal from 'components/modal/HkModal';
import { Asset } from 'core/types';
import './InventoryAnalyzeImageModal.scss';

interface InventoryAnalyzeImageModalProps {
  isOpen: boolean;
  photos: Asset[];
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (photo: Asset) => void;
}

const displayAssetThumbnail = (asset: Asset) =>
  asset.thumb_cloud_store ? asset.thumb_cloud_store : asset.file_url;

/**
 * "Analyze Image with AI" modal — reached from the item detail's Actions
 * menu. Only the product's own photos are offered here (never catalog
 * template stock photos, which won't have this specific unit's serial/model
 * label on them); the homeowner picks which one to scan before any OCR call
 * is made, mirroring InventoryDeleteConfirmModal.tsx's structure/style.
 */
const InventoryAnalyzeImageModal: React.FC<InventoryAnalyzeImageModalProps> = ({
  isOpen,
  photos,
  loading,
  onCancel,
  onConfirm
}) => {
  const [selected, setSelected] = useState<Asset | null>(null);

  // Re-derives the default selection each time the modal opens — `photos` is
  // recomputed by the caller on every render, so it can't be a dep without
  // re-running (and re-selecting) on every keystroke elsewhere in the form.
  useEffect(() => {
    if (isOpen) {
      setSelected(photos.length === 1 ? photos[0] : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <HkModal
      className="hk-inventory-analyze-image-modal"
      isOpen={isOpen}
      title="Analyze Image with AI"
      onClose={onCancel}
      footer={
        <div className="hk-inventory-analyze-image-modal-actions">
          <IonButton
            className="hk-inventory-analyze-image-modal-cancel-button"
            fill="outline"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </IonButton>
          <IonButton
            className="hk-inventory-analyze-image-modal-analyze-button"
            color="primary"
            disabled={!selected}
            onClick={() => selected && !loading && onConfirm(selected)}
          >
            {loading ? (
              <>
                <IonSpinner name="dots" />
                Analyzing
              </>
            ) : (
              'Analyze'
            )}
          </IonButton>
        </div>
      }
    >
      <IonText className="hk-inventory-analyze-image-modal-question">
        Select a photo for AI to scan for Serial # and Model # information
      </IonText>
      <div className="hk-inventory-analyze-image-modal-grid">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className={`hk-inventory-analyze-image-modal-thumb${
              selected?.id === photo.id
                ? ' hk-inventory-analyze-image-modal-thumb-selected'
                : ''
            }`}
            role="button"
            tabIndex={0}
            aria-pressed={selected?.id === photo.id}
            onClick={() => setSelected(photo)}
            onKeyDown={(event) => {
              // Picking a photo is required to enable Analyze below, so without
              // a key handler the whole OCR flow is unreachable by keyboard.
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setSelected(photo);
              }
            }}
          >
            <img src={displayAssetThumbnail(photo)} alt={photo.name} />
            {selected?.id === photo.id && (
              <IonIcon
                icon={checkmarkCircle}
                className="hk-inventory-analyze-image-modal-thumb-check"
              />
            )}
          </div>
        ))}
      </div>
    </HkModal>
  );
};

export default InventoryAnalyzeImageModal;
